import React, { useState, useRef } from "react";
import logo from "../assets/images/logo.png";
import cancel from "../assets/images/navbar/cancel.png";
import arrow from "../assets/images/order/orange-arrow.png";

type DeliveryOtpModalProps = {
  isOpen: boolean;
  onClose: () => void;
  expectedOtp: string;
  onVerifySuccess: () => void;
};

const DeliveryOtpModal: React.FC<DeliveryOtpModalProps> = ({
  isOpen,
  onClose,
  expectedOtp,
  onVerifySuccess,
}) => {
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const [error, setError] = useState<string>("");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  if (!isOpen) return null;

  console.log("expectedOtp", expectedOtp);

  const handleOtpChange = (index: number, value: string) => {
    if (!/^[0-9]*$/.test(value)) return; // Only allow numbers

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Take only the last character
    setOtp(newOtp);
    setError(""); // Clear error when user types

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = () => {
    const otpValue = otp.join("");

    if (otpValue.length !== 6) {
      setError("Please enter the complete 6-digit OTP");
      return;
    }

    if (otpValue === expectedOtp.toString()) {
      // OTP matched
      setOtp(["", "", "", "", "", ""]);
      setError("");
      onVerifySuccess();
      onClose();
    } else {
      // OTP not matched
      setError("OTP not matched. Please try again.");
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    }
  };

  const handleClose = () => {
    setOtp(["", "", "", "", "", ""]);
    setError("");
    onClose();
    window.location.reload();
  };

  return (
    <>
      {/* Backdrop Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[1003]"
        onClick={handleClose}
      />

      {/* Modal Container */}
      <div className="fixed inset-0 flex items-center justify-center z-[1004] pointer-events-none">
        <div
          className="w-full h-full md:w-[545px] md:h-auto p-6 md:p-8 rounded-none md:rounded-[24px] bg-white pointer-events-auto relative"
          style={{
            boxShadow: "-23px 23px 72px 0 rgba(0, 0, 0, 0.15)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <div
            className="absolute top-4 right-4 md:top-6 md:right-6 h-[36px] w-[36px] cursor-pointer border rounded-full p-[7px] bg-white flex items-center justify-center"
            onClick={handleClose}
          >
            <img src={cancel} alt="Close" className="h-[18px] w-[18px]" />
          </div>

          {!expectedOtp ? (
            <div className="flex flex-col items-center">
              <p className="text-[#6A6A6A] font-Urbanist text-sm md:text-[16px] font-medium mb-6">
                No delivery partner assigned yet{" "}
              </p>
              <button
                onClick={handleClose}
                className="w-1/2 flex items-center justify-center py-2 px-3 bg-[#FF8A00] rounded-[150px] cursor-pointer hover:bg-[#E67A00] transition-colors"
              >
                <span className="text-white font-Urbanist text-[20px] font-semibold leading-[140%]">
                  OK
                </span>
              </button>
            </div>
          ) : (
            <div>
              {/* Logo */}
              <div className="mb-6 md:mb-[36px] mt-4 md:mt-0">
                <img src={logo} alt="Duddus" className="h-[54px] w-[166px]" />
              </div>

              {/* Title */}
              <h2 className="text-[#050505] font-Urbanist text-2xl md:text-[32px] font-semibold leading-[125%] mb-2 md:mb-[8px]">
                Verify Delivery OTP
              </h2>

              {/* Subtitle */}
              <p className="text-[#6A6A6A] font-Urbanist text-sm md:text-[16px] font-medium leading-[140%] mb-6 md:mb-[24px]">
                Enter the 6-digit OTP to mark order as out for delivery
              </p>

              {/* OTP Input Boxes */}
              <div className="flex justify-center gap-2 md:gap-3 mb-4">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      inputRefs.current[index] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className={`w-[45px] h-[45px] md:w-[60px] md:h-[60px] text-center text-[20px] md:text-[24px] font-semibold border-b-2 rounded-[12px] outline-none transition-colors shadow-[0_1px_4px_0_rgba(229,229,229,0.40)] bg-[#FAFAFA] ${
                      error
                        ? "border-red-500"
                        : "border-[#E0E0E0] focus:border-[#FF8A00]"
                    }`}
                  />
                ))}
              </div>

              {/* Error Message */}
              {error && (
                <div className="text-center mb-4">
                  <p className="text-red-500 font-Urbanist text-sm md:text-[14px] font-medium">
                    {error}
                  </p>
                </div>
              )}

              <div className="mb-8 md:mb-[60px]"></div>

              {/* Verify Button */}
              <button
                onClick={handleVerify}
                className="w-full flex items-center justify-between py-4 px-6 bg-[#FF8A00] rounded-[150px] cursor-pointer hover:bg-[#E67A00] transition-colors"
              >
                <span className="text-white font-Urbanist text-[20px] font-semibold leading-[140%]">
                  Verify & Proceed
                </span>
                <div className="h-[42px] w-[42px] flex justify-center items-center bg-white rounded-full">
                  <img
                    src={arrow}
                    alt="right arrow"
                    className="h-[41px] w-[30px] stroke-[2.587px] stroke-[#FB8606]"
                  />
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DeliveryOtpModal;
